import type { ModelError } from "@iot-platform/core";
import { NonRecuperableModelError } from "@iot-platform/core";
import type {
    SensorAlertDto,
    SensorAlertsDto,
} from "@station/data/dto/sensor-alert.dto";
import {
    SensorAlertDtoSchema,
    SensorAlertsDtoSchema,
} from "@station/data/dto/sensor-alert.dto";
import {
    SensorAlert,
    SensorAlerts,
} from "@station/domain/models/sensor-alert.model";
import {
    FormatEnum,
    Sensor,
    SeriesTypeEnum,
} from "@station/domain/models/sensor.model";

import { injectable } from "inversify";
import { err, Result } from "neverthrow";

@injectable()
export class SensorAlertMapper {
    map(
        sensorAlertDto: SensorAlertDto,
    ): Result<SensorAlert, ModelError<typeof SensorAlert>> {
        const sensorAlertDtoParsed =
            SensorAlertDtoSchema.safeParse(sensorAlertDto);

        if (!sensorAlertDtoParsed.success) {
            console.error(sensorAlertDtoParsed.error.issues);
            return err(new NonRecuperableModelError());
        }

        const { variable_alerts } = sensorAlertDto;

        const mappedVariableAlerts = variable_alerts.map((variableAlertDto) => {
            const sensorDto = variableAlertDto.variable;

            const sensorProps = {
                id: sensorDto.id,
                code:
                    sensorDto.code === undefined || sensorDto.code === ""
                        ? null
                        : sensorDto.code,
                name: sensorDto.name,
                extendedName: sensorDto.extended_name ?? null,
                format: sensorDto.format ? FormatEnum[sensorDto.format] : null,
                allowAccumulatedValues:
                    sensorDto.allow_accumulated_values ?? false,
                icon: sensorDto.icon ?? null,
                symbol: sensorDto.symbol ?? null,
                graphable: sensorDto.graphable ?? false,
                datable: sensorDto.datable ?? false,
                color: sensorDto.color ?? null,
                minThreshold: sensorDto.min_threshold ?? null,
                maxThreshold: sensorDto.max_threshold ?? null,
                goodThreshold: sensorDto.good_threshold ?? false,
                showTendency: sensorDto.show_tendency ?? false,
                allowReportTendency: sensorDto.allow_report_tendency ?? false,
                allowReportSum: sensorDto.allow_report_sum ?? false,
                seriesType: sensorDto.series_type
                    ? SeriesTypeEnum[sensorDto.series_type]
                    : null,
                variablePlotBands: sensorDto.variable_plot_bands
                    ? sensorDto.variable_plot_bands.map((plotBandDto) => ({
                          color: plotBandDto.color,
                          label: plotBandDto.label,
                          from: plotBandDto.from,
                          to: plotBandDto.to,
                      }))
                    : [],
                customPlotBands: sensorDto.custom_plot_bands
                    ? sensorDto.custom_plot_bands.map((plotBandDto) => ({
                          color: plotBandDto.color,
                          label: plotBandDto.label,
                          from: plotBandDto.from,
                          to: plotBandDto.to,
                      }))
                    : [],
                measurementUnitId: sensorDto.measurement_unit_id ?? null,
                effectiveRange: sensorDto.effective_range ?? null,
            };

            return {
                variableAlertDto,
                sensor: Sensor.createWithRecovery(sensorProps).match(
                    (sensor) => sensor,
                    (error) => (error.recuperable ? error.instance : null),
                ),
            };
        });

        const validVariableAlerts = mappedVariableAlerts.mapNotNull(
            ({ variableAlertDto, sensor }) =>
                sensor === null
                    ? null
                    : {
                          id: variableAlertDto.id,
                          hoursElapsedToTrigger:
                              variableAlertDto.hours_elapsed_to_trigger,
                          lowTriggerValue:
                              variableAlertDto.low_trigger_value ?? undefined,
                          highTriggerValue:
                              variableAlertDto.high_trigger_value ?? undefined,
                          sensor,
                          activeInStationsLabel:
                              variableAlertDto.active_in_stations_label,
                          activeInStationsIds:
                              variableAlertDto.active_in_stations_ids ?? [],
                      },
        );

        return SensorAlert.createWithRecovery({
            id: sensorAlertDto.id,
            name: sensorAlertDto.name,
            uniqueTrigger: sensorAlertDto.unique_trigger,
            notifyAll: sensorAlertDto.notify_all,
            isEnabled: sensorAlertDto.is_enabled,
            stations: sensorAlertDto.stations,
            lastNotificationAt: sensorAlertDto.last_notification_at,
            stationIds: sensorAlertDto.station_ids,
            variableAlerts: validVariableAlerts,
        });
    }

    mapList(sensorAlertsDto: SensorAlertsDto): SensorAlerts {
        const sensorAlertsDtoParsed =
            SensorAlertsDtoSchema.safeParse(sensorAlertsDto);

        if (!sensorAlertsDtoParsed.success) {
            console.error(sensorAlertsDtoParsed.error.issues);
            return SensorAlerts.empty();
        }

        const sensorAlerts = sensorAlertsDto.mapNotNull((sensorAlertDto) =>
            this.map(sensorAlertDto).match(
                (sensorAlert) => sensorAlert,
                (error) => {
                    if (!error.recuperable) {
                        console.error(
                            "Non-recoverable error mapping sensor alerts:",
                            error,
                        );
                    }
                    return null;
                },
            ),
        );

        return SensorAlerts.create({
            sensorAlerts,
            count: sensorAlerts.length,
        }).unwrapOr(SensorAlerts.empty());
    }
}
